import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SelectedState, valuesProps } from "types/paymentMethods";
import DiscoverIcon from "icons/payments/Discover";
import { MastercardIcon } from "icons/payments/MastercardIcon";
import UnionPay from "icons/payments/UnionPay";
import { VisaIcon } from "icons/payments/VisaIcon";
import JCB from "icons/payments/JCB.svg";
import amex from "icons/payments/amex.svg";
import dinersClub from "icons/payments/dinersClub.svg";
import { CountryOption, StateOption } from "types/common";
import {
  countryListGet,
  staxProfile,
  useCountryList,
} from "store/slices/paymentMethodSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { AddressDetails, countryAddressList } from "utils/countryAddressList";
import { countryAndState } from "store/slices/documentsSlice";
import {
  checkAddress,
  checkZip,
  detectCountry,
  getCountry3digitCode,
  validateCard,
} from "utils/HelperFunctions";
import InputField from "components/form/InputField";
import { SettingsValue } from "types/payments";
import InputSelect from "components/form/InputSelect";
import Button from "components/form/Button";
import { RootState } from "store/store";
import { useUserData } from "store/slices/authSlice";
import { CountryCode } from "types/UserProfile";
import { useTid } from "utils/testid";

type CardBrand =
  | "visa"
  | "amex"
  | "mc"
  | "mastercard"
  | "discover"
  | "ds"
  | "jcb"
  | "diners"
  | "dinersclub"
  | "diners club"
  | "dc"
  | "unionpay"
  | string;

interface CheckoutFormProps {
  handleChange: (
    e:
      | ChangeEvent<HTMLInputElement>
      | { value?: string; name?: string }
      | { target?: { value?: any } },
    value: string
  ) => void;
  isEdit?: boolean;
  values: valuesProps;
  iconType: string;
  isDateErr: boolean;
  selectedState: SelectedState | undefined;
  setSelectedState: (state: SelectedState | undefined) => void;
  disableInputFields?: boolean;
  show?: boolean;
  setIconType: (val: string) => void;
  accountid?: string;
  clusterId?: string;
  email?: string;
  leadId?: string | number;
  setSuccessScreen: (val: boolean) => void;
}

const AddCardStaxForm: React.FC<CheckoutFormProps> = ({
  handleChange,
  isEdit,
  values,
  iconType,
  selectedState,
  setSelectedState,
  disableInputFields,
  show,
  setIconType,
  accountid,
  clusterId,
  leadId,
  email,
  setSuccessScreen,
}) => {
  const tid = useTid();
  const [isLoading, setIsLoading] = useState(false);
  const [isErr, setIsErr] = useState<number>(-1);
  const countryList: CountryOption[] = useCountryList();
  const dispatch = useAppDispatch();
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const portalSettings = usePortalSettingsData();
  const [fieldArr, setFieldArr] = useState<string[] | undefined>();
  const [fieldObj, setFieldObj] = useState<AddressDetails | undefined>();
  const [states, setStates] = useState<StateOption[]>([]);
  const [countriesWithStates, setCountriesWithStates] = useState<any[]>([]);
  const [cardFormComplete, setCardFormComplete] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);

  const staxRef = useRef<any>(null);
  const scriptLoaded = useRef(false);
  const initCountryRef = useRef<boolean>(false);
  const autoDetectRef = useRef<boolean>(false);
  const userData = useUserData();

  const onCountryListGet = async (): Promise<void> => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.log("conntry list get err: -->", error?.message);
    }
  };

  useEffect(() => {
    autoDetectRef.current = false;
  }, []);

  const setCountry = async (): Promise<void> => {
    const userCountry = await detectCountry();
    if (userCountry) handleChange({ value: userCountry }, "x_country");
  };

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
      return;
    }
    if (
      !isEdit &&
      !autoDetectRef.current &&
      (!values?.x_country || values?.x_country === "US")
    ) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [countryList]);

  useEffect(() => {
    if (values?.x_country) {
      let data = countryAddressList()[values?.x_country || "US"];
      if (data) {
        const arr = Object.keys(data).map((field) => field);
        const obj = countryAddressList()[values?.x_country || "US"];
        setFieldArr(arr);
        setFieldObj(obj);
      }
    }
    if (isEdit && values?.x_country) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
      if (values?.x_state) {
        const selectedStateObject: any = nStates.find(
          (state: any) => state.name === values?.x_state
        );
        if (selectedStateObject) {
          setSelectedState(selectedStateObject);
          handleChange(selectedStateObject?.name, "x_state");
        }
      }
    } else if (isEdit && !values?.x_country) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [isEdit, values?.x_country]);

  useEffect(() => {
    if (show && !isEdit && countryList?.length > 0) {
      setCountry();
      autoDetectRef.current = true;
    }
  }, [show, isEdit, countryList]);

  const getPostalCode = (): string | false => {
    if (fieldArr && fieldArr.includes("CEDEX")) return "CEDEX";
    if (fieldArr && fieldArr.includes("ZIP")) return "ZIP";
    if (fieldArr && fieldArr.includes("Eircode")) return "Eircode";
    if (fieldArr && fieldArr.includes("PIN")) return "PIN";
    if (fieldArr && fieldArr.includes("Postal code")) return "Postal code";
    return false;
  };

  const getStateName = (): string | false => {
    if (fieldArr && fieldArr.includes("County")) return "County";
    if (fieldArr && fieldArr.includes("State")) return "State";
    if (fieldArr && fieldArr.includes("Department")) return "Department";
    if (fieldArr && fieldArr.includes("District")) return "District";
    if (fieldArr && fieldArr.includes("Emirate")) return "Emirate";
    if (fieldArr && fieldArr.includes("Island")) return "Island";
    if (fieldArr && fieldArr.includes("Oblast")) return "Oblast";
    if (fieldArr && fieldArr.includes("Parish")) return "Parish";
    if (fieldArr && fieldArr.includes("Prefecture")) return "Prefecture";
    if (fieldArr && fieldArr.includes("Province")) return "Province";
    return false;
  };
  const selectedCountryUI = useMemo(
    () => values?.x_country ?? "",
    [values?.x_country]
  );

  useEffect(() => {
    const getCountryAndState = async () => {
      try {
        const res: any = await dispatch(countryAndState()).unwrap();
        const data = res?.body?.data || [];
        setCountriesWithStates(data);
      } catch (error) {
        console.error(error);
      }
    };

    getCountryAndState();
  }, []);

  const handleBadgeDisplay = (show?: boolean) => {
    const badge = document.querySelector(".grecaptcha-badge");
    if (badge && badge instanceof HTMLElement) {
      badge.style.display = show ? "block" : "none";
    }
  };

  useEffect(() => {
    if (show) {
      handleBadgeDisplay(true);
    } else {
      handleBadgeDisplay(false);
    }
  }, [show]);

  const getStatesForCountryCode = (code: string): StateOption[] => {
    try {
      const countryMeta = countryList?.find((c) => c?.value === code);
      const countryName = countryMeta?.name;
      if (!countryName) return [];
      const found = countriesWithStates?.find(
        (c: any) =>
          c?.country_name?.toLowerCase() === countryName?.toLowerCase()
      );
      const st = found?.states || [];
      return st.map((s: { state_name: string; state_code: string }) => ({
        name: s?.state_name,
        value: s?.state_name,
        state_code: s?.state_code,
      }));
    } catch (e) {
      return [];
    }
  };

  useEffect(() => {
    if (
      values?.x_country &&
      countriesWithStates?.length > 0 &&
      countryList?.length > 0
    ) {
      const nStates = getStatesForCountryCode(values?.x_country);
      setStates(nStates);
    }
  }, [values?.x_country, countriesWithStates, countryList]);

  useEffect(() => {
    if (
      isEdit &&
      !initCountryRef.current &&
      selectedCountryUI &&
      countryList?.length > 0
    ) {
      const ev = countryList.find((c: any) => c?.value === selectedCountryUI);
      if (ev) {
        const nStates = getStatesForCountryCode(selectedCountryUI as string);
        setStates(nStates);
        handleChange(ev, "x_country");
        initCountryRef.current = true;
      }
    }
  }, [isEdit, selectedCountryUI, countryList, handleChange]);

  const isDisabled = useMemo<boolean>(() => {
    const addressField = checkAddress(
      portalSettings?.cc_billing_address,
      portalSettings?.cc_billing_addr_mandatory,
      values
    );
    const validZip = checkZip(values);
    const isErr = tokenError && tokenError?.trim()?.length > 0;

    if (isEdit) {
      return (
        !addressField ||
        !values?.choldername ||
        !values?.choldername.trim() ||
        !validZip
      );
    } else {
      return (
        !cardFormComplete ||
        !addressField ||
        isErr ||
        !values?.choldername ||
        !values?.choldername.trim() ||
        !validZip
      );
    }
  }, [cardFormComplete, values, portalSettings, tokenError]);

  const getStateAndCountryCode = (countryCode: string, stateName: string) => {
    const ISOCode = getCountry3digitCode(countryCode as CountryCode);
    const statesList = states.find((s: any) => s.name === stateName);
    const stateCode = statesList?.state_code || "";

    return {
      country_code: ISOCode || countryCode,
      state_code: stateCode || stateName,
    };
  };

  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [month, year] = values?.x_exp_date?.split("/") || ["", ""];
      const namesSplit = values?.choldername?.trim().split(" ") || [];

      const { country_code, state_code } = getStateAndCountryCode(
        values?.x_country || "US",
        values?.x_state || ""
      );

      let payload: any = {
        firstname: namesSplit?.[0] || userData?.data?.accountData?.first_name,
        lastname: namesSplit?.[1] || userData?.data?.accountData?.last_name,
        month: month?.trim(),
        year: `20${year?.trim()}`,
        person_name:
          values?.choldername ||
          `${userData?.data?.accountData?.first_name} ${userData?.data?.accountData?.last_name}`,
        method: "card",
        validate: false,
        email: email || userData?.data?.accountData?.email,
        phone: userData?.data?.accountData?.phone,
        // address_1: values?.x_address,
        // address_2: values?.address2 || "",
        // address_city: values?.x_city,
        // address_state: state_code,
        // address_zip: values?.x_zip,
        // address_country: country_code,
      };

      if (values?.x_address) {
        payload = {
          ...payload,
          address_1: values?.x_address,
        };
      }

      if (values?.x_city) {
        payload = {
          ...payload,
          address_city: values?.x_city,
        };
      }
      if (country_code) {
        payload = {
          ...payload,
          address_country: country_code,
        };
      }
      if (state_code) {
        payload = {
          ...payload,
          address_state: state_code,
        };
      }
      if (values?.x_zip) {
        payload = {
          ...payload,
          address_zip: values?.x_zip,
        };
      }

      const result = await staxRef.current.tokenize(payload);

      let dataObj = {
        cc_token: result?.id,
        cc_token2: result?.customer_id,
        lead_id: leadId,
        email: email || userData?.data?.accountData?.email,
        module: "payment_method",
        x_method: "CC",
        clusterid: clusterId,
        accountid: accountid,
      };

      const response: any = await dispatch(staxProfile(dataObj)).unwrap();
      if (response?.body?.success) {
        setSuccessScreen(true);
      } else {
        setTokenError(response?.body?.data?.message || null);
      }
    } catch (err: any) {
      console.log("card error===", err);
      const error = err.fieldErrors || [];
      if (error?.length > 0) {
        setTokenError(error[0]?.message || "Card details error");
        setErrorField(error[0]?.field || null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const imgUrl = (): React.ReactNode => {
    const brand: CardBrand = (
      values?.card_type ||
      iconType ||
      ""
    )?.toLowerCase();
    switch (brand) {
      case "visa":
        return <VisaIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="Amex" />;
      case "mc":
      case "mastercard":
      case "master":
        return <MastercardIcon />;
      case "discover":
      case "ds":
        return <DiscoverIcon />;
      case "jcb":
        return <img src={JCB} alt="JCB" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="Diners Club" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return null;
    }
  };
  const initStax = async () => {
    try {
      const webPaymentToken =
        paymentPermission?.cc_gateway_name?.key || "Test-Merchant-7e3f2cd81dbe";

      const stax = new window.StaxJs(webPaymentToken, {
        number: {
          id: "stax-card-number",
          placeholder: "1234 1234 1234 1234",
          style:
            "font-family:'DM Sans' !important;width:100%;height:100%;border:none;background:transparent;font-size:12px;",
        },
        cvv: {
          id: "stax-card-cvv",
          placeholder: "CVC",
          style:
            "font-family:'DM Sans' !important ;width:100%;height:100%;border:none;background:transparent;font-size:12px;",
        },
      });

      staxRef.current = stax;

      await stax.showCardForm();

      stax.on("card_form_complete", () => {
        setCardFormComplete(true);
        setTokenError(null);
        setErrorField(null);
        console.log("Card form complete");
      });

      stax.on("card_form_uncomplete", () => {
        setCardFormComplete(false);
        console.log("Card form incomplete");
      });

      console.log("Stax initialized");
    } catch (err) {
      console.error("Stax initialization error:", err);
    }
  };

  useEffect(() => {
    if (scriptLoaded.current || !show) return;

    const script = document.createElement("script");
    script.src = "https://staxjs.staxpayments.com/staxjs-captcha.js";
    script.async = true;

    script.onload = () => initStax();

    document.body.appendChild(script);
    scriptLoaded.current = true;

    return () => {
      document.body.removeChild(script);
    };
  }, [show]);

  return (
    <form
      data-testid={tid("stax-card-form")}
      aria-label={isEdit ? "Update Stax card form" : "Add Stax card form"}
    >
      <div
        className="lg:px-10 px-5 w-full space-y-4"
        data-testid={tid("stax-card-fields")}
      >
        {/* Card details */}
        <div className="w-full" data-testid={tid("stax-card-section")}>
          <div className="relative">
            <h2 className="text-txtSecondary text-xs font-bold uppercase mb-1">
              CARD INFORMATION
            </h2>

            <div
              className={`flex items-center border ${errorField === "stax-card-number" ? "!border-red" : ""} !rounded-[5px] !rounded-b-none !pl-[15px] !pr-[15px] !py-2 text-xs`}
              data-testid={tid("stax-card-number-element")}
              aria-label="Card number"
            >
              <div
                id="stax-card-number"
                className="flex-1 h-[20px]"
                data-testid={tid("stax-card-number-hosted-field")}
              />

              {iconType?.length > 0 && (
                <div
                  className={`absolute  right-4 top-1/2 transform -translate-y-1/2 text-xs capitalize ${
                    iconType ? "w-35px" : "w-174px"
                  }`}
                >
                  {imgUrl()}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2">
            <InputField
              name="x_exp_date"
              showLabel={false}
              className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !rounded-e-none  ${
                isErr === 2 ? "!border-red" : isErr === 1 ? "!border-t-0" : ""
              }`}
              _className="!text-xs !font-bold !leading-normal"
              placeholder="MM / YY"
              value={values?.x_exp_date}
              onChange={(e) => {
                handleChange(e, "x_exp_date");
              }}
              onBlur={() => {
                validateCard({
                  values,
                  iconType,
                  setIsErr,
                  setCardErrMsg: setTokenError,
                });
              }}
              disabled={disableInputFields || isEdit}
              data-testid={tid("stax-card-expiry-input")}
              aria-label="Card expiration date"
            />

            <div
              className={`flex items-center border ${errorField === "stax-card-cvv" ? "!border-red" : ""} !pl-[15px] !pr-[15px] !py-2 text-xs !rounded-[5px] !rounded-t-none !rounded-s-none`}
              data-testid={tid("stax-card-cvc-element")}
              aria-label="Card security code"
            >
              <div
                id="stax-card-cvv"
                className="w-full h-[15px]"
                data-testid={tid("stax-card-cvc-hosted-field")}
              />
            </div>
          </div>
        </div>

        {/* Cardholder name */}
        <div className="w-full" data-testid={tid("stax-cardholder-section")}>
          <InputField
            name="choldername"
            label="Cardholder name"
            className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] "
            _className="!text-xs !font-bold !leading-normal"
            placeholder="Full name on card"
            value={values?.choldername}
            onChange={(e) => handleChange(e, "choldername")}
            tabInd={4}
            disabled={disableInputFields}
            data-testid={tid("stax-cardholder-name-input")}
            aria-label="Cardholder name"
          />
        </div>

        {portalSettings?.cc_billing_address === SettingsValue.YES && (
          <div
            className="w-full"
            data-testid={tid("stax-billing-address-section")}
          >
            <InputSelect
              label="Billing Address"
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              arrowClassName="!right-[11px] !top-[10px]"
              placeholder="Country"
              selected={selectedCountryUI}
              options={countryList}
              setSelected={(e: any) => {
                autoDetectRef.current = true;
                const nStates = getStatesForCountryCode(e?.value as string);
                setStates(nStates);
                setSelectedState(undefined);
                handleChange({ target: { value: "" } }, "x_state");
                handleChange(e, "x_country");
              }}
              tabInd={5}
              disabled={disableInputFields}
              data-testid={tid("stax-billing-country-select")}
              aria-label="Billing country"
            />
            {fieldArr && fieldArr.includes("Address line 1") && (
              <InputField
                name="x_address"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                  (fieldArr &&
                    (fieldArr.includes("City") ||
                      fieldArr.includes("Area") ||
                      fieldArr.includes("Address line 2"))) ||
                  getPostalCode() ||
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Address line 1"
                value={values?.x_address}
                onChange={(e) => handleChange(e, "x_address")}
                tabInd={6}
                disabled={disableInputFields}
                data-testid={tid("stax-billing-address-line-1-input")}
                aria-label="Billing address line 1"
              />
            )}
            {fieldArr && fieldArr.includes("Address line 2") && (
              <InputField
                name="address2"
                showLabel={false}
                className={`!pl-[15px] !pr-[15px] !py-2 !text-xs  !border-t-0 ${
                  (fieldArr &&
                    (fieldArr.includes("City") || fieldArr.includes("Area"))) ||
                  getPostalCode() ||
                  getStateName()
                    ? "!rounded-none"
                    : "!rounded-b-[5px] !rounded-t-none"
                }`}
                _className="!text-xs !font-bold !leading-normal"
                placeholder="Address line 2"
                value={values?.address2}
                onChange={(e) => handleChange(e, "address2")}
                tabInd={7}
                disabled={disableInputFields}
                data-testid={tid("stax-billing-address-line-2-input")}
                aria-label="Billing address line 2"
              />
            )}
            <div
              className={`grid ${
                getPostalCode()
                  ? !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                    ? "grid-cols-1"
                    : "grid-cols-2"
                  : "grid-cols-1"
              }`}
            >
              {fieldArr && fieldArr.includes("City") && (
                <InputField
                  name="x_city"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                    getStateName()
                      ? "!rounded-none"
                      : getPostalCode()
                        ? "rounded-b-[5px] !rounded-r-none"
                        : "!rounded-[5px] !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="City"
                  value={values?.x_city}
                  onChange={(e) => handleChange(e, "x_city")}
                  tabInd={8}
                  disabled={disableInputFields}
                  data-testid={tid("stax-billing-city-input")}
                  aria-label="Billing city"
                />
              )}
              {fieldArr && fieldArr.includes("Area") && (
                <InputField
                  name="x_city"
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-t-none !border-t-0 ${
                    getStateName()
                      ? "!rounded-none"
                      : getPostalCode()
                        ? "rounded-b-[5px] !rounded-r-none"
                        : "!rounded-[5px] !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder="Area"
                  value={values?.x_city}
                  onChange={(e) => handleChange(e, "x_city")}
                  tabInd={8}
                  disabled={disableInputFields}
                  data-testid={tid("stax-billing-area-input")}
                  aria-label="Billing area"
                />
              )}

              {/* ['CEDEX' , 'ZIP' , 'Eircode' , 'PIN', "Postal code" ] */}
              {getPostalCode() && (
                <InputField
                  name="x_zip"
                  type={fieldObj?.isNumber ? "number" : "text"}
                  showLabel={false}
                  className={`!pl-[15px] !pr-[15px] !py-2 !text-xs !border-t-0 ${
                    !fieldArr?.includes("City") && !fieldArr?.includes("Area")
                      ? ""
                      : "!border-l-0"
                  } ${
                    getStateName()
                      ? "!rounded-none"
                      : "!rounded-b-[5px] !rounded-l-none !rounded-t-none"
                  }`}
                  _className="!text-xs !font-bold !leading-normal"
                  placeholder={getPostalCode() || ""}
                  value={values?.x_zip}
                  onChange={(e) => {
                    if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                      handleChange(e, "x_zip");
                  }}
                  tabInd={9}
                  disabled={disableInputFields}
                  data-testid={tid("stax-billing-postal-code-input")}
                  aria-label="Billing postal code"
                />
              )}
            </div>
            {states && states.length > 0 && (
              <InputSelect
                showLabel={false}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-t-none !border-t-0 "
                _className="!text-xs !font-bold !leading-normal"
                optionClassName="!text-xs"
                placeholder={getStateName() || "State"}
                selected={selectedState}
                arrowClassName="!right-[11px] !top-[10px]"
                isManual
                options={states}
                setSelected={(e: any) => {
                  if (e?.value) {
                    setSelectedState(e);
                    handleChange(e, "x_state");
                  }
                }}
                tabInd={10}
                disabled={disableInputFields}
                data-testid={tid("stax-billing-state-select")}
                aria-label="Billing state"
              />
            )}
          </div>
        )}

        {portalSettings?.cc_billing_address === SettingsValue.NO && (
          <div
            className="w-full"
            data-testid={tid("stax-billing-postal-section")}
          >
            <InputSelect
              label="Billing Address"
              className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-[5px] !rounded-b-none "
              _className="!text-xs !font-bold !leading-normal"
              optionClassName="!text-xs"
              arrowClassName="!right-[11px] !top-[10px]"
              placeholder="Country"
              selected={selectedCountryUI}
              options={countryList}
              setSelected={(e: any) => {
                autoDetectRef.current = true;
                const nStates = getStatesForCountryCode(e?.value as string);
                setStates(nStates);
                setSelectedState(undefined);
                handleChange({ target: { value: "" } }, "x_state");
                handleChange(e, "x_country");
              }}
              tabInd={5}
              disabled={disableInputFields}
              data-testid={tid("stax-billing-country-select")}
              aria-label="Billing country"
            />
            {getPostalCode() && (
              <InputField
                name="x_zip"
                type={fieldObj?.isNumber ? "number" : "text"}
                showLabel={false}
                className="!pl-[15px] !pr-[15px] !py-2 !text-xs !rounded-b-[5px] !border-t-0 !rounded-t-none "
                _className="!text-xs !font-bold !leading-normal"
                placeholder={getPostalCode() || ""}
                value={values?.x_zip}
                onChange={(e) => {
                  if (e.target.value?.length <= (fieldObj?.zipLength || 50))
                    handleChange(e, "x_zip");
                }}
                tabInd={9}
                disabled={disableInputFields}
                data-testid={tid("stax-billing-postal-code-input")}
                aria-label="Billing postal code"
              />
            )}
          </div>
        )}
        {tokenError ? (
          <div className="text-red text-xs mt-0.5">{tokenError}</div>
        ) : isErr === 2 ? (
          <div className="text-red text-xs mt-0.5">{tokenError}</div>
        ) : (
          <></>
        )}
      </div>

      <div className="lg:px-10 px-5 pt-5">
        <Button
          text={isEdit ? "Update Card" : "Add Card"}
          className="w-full"
          isLoading={isLoading}
          isDisabled={isDisabled}
          onClick={handleSubmit}
          data-testid={tid("stax-card-submit-button")}
          aria-label={isEdit ? "Update Stax card" : "Add Stax card"}
        />
      </div>
    </form>
  );
};

export default AddCardStaxForm;
